
import * as React from 'react';
import LanguageList from './LanguageList';
import Progress from './Progress';
import Error from './Error';

import KeycloakService from '../services/KeycloakService';

require('es6-promise').polyfill();

export interface AppProps {
    title: string;
    isOfficeInitialized: boolean;
}

export enum DocumentLoadingState {
    initialized,
    loading,
    successful,
    failed
}
export interface AppState {
    authenticated: boolean;
    documentLoadingState: DocumentLoadingState;
    documentUrl: string;
    documentId: string;
}

export default class App extends React.Component<AppProps, AppState> {
    static keycloakAuth: KeycloakService = new KeycloakService();

    constructor(props, context) {
        super(props, context);
        this.setAuthenticated = this.setAuthenticated.bind(this);
        this.getQueryStringValue = this.getQueryStringValue.bind(this);

        this.initiateLogin = this.initiateLogin.bind(this);

        this.state = {
            authenticated: false,
            documentId: '',
            documentLoadingState: DocumentLoadingState.initialized,
            documentUrl: ''
        };
    }

    initiateLogin(onLoggedIn) {
        // We start off in the Add-in (Parent). We are going to open an auth flow (Dialog).
        // The Parent and Dialog are separate browser windows, which can communicate through messageParent.
        // Each window loads this same code. That convolutes this flow, but what happens is:
        // [Parent] Init (unauthorized)
        // [Parent] Opening dialog.
        // [Dialog] Init (first time, unauthorized)
        // [Dialog] Sending to keycloak.
        // [Keycloak] Do auth, redirect back.
        // [Dialog] Init (second time, authorized)
        // [Dialog] Back from keycloak. (forward tokens to parent)
        // [Parent] Closing dialog. (*Re*Init, with tokens from Dialog)

        const inDialog = Office && Office.context && Office.context.ui && Office.context.ui.messageParent;
        const messageParent = function (obj) {
            Office.context.ui.messageParent(JSON.stringify(obj));
        }
        const logAuth = function (msg:string, obj?: Object) {
            if (inDialog) {
                if (obj) {
                    messageParent({
                        action: 'log-object',
                        msg: "[AUTH] [Dialog] " + msg,
                        obj: obj
                    });
                } else {
                    messageParent({
                        action: 'log',
                        msg: "[AUTH] [Dialog] " + msg
                    });
                }
            } else {
                if (obj) {
                    console.log("[AUTH] [Parent] " + msg, obj);
                } else {
                    console.log("[AUTH] [Parent] " + msg);
                }
            }
        };

        logAuth("Starting init")
        App.keycloakAuth.init()
           .then(function (authenticated:boolean) {
               logAuth("Initialized");
               if (inDialog) {
                   if (!authenticated) {
                       logAuth("Sending to keycloak.");
                       App.keycloakAuth.login();
                   } else {
                       logAuth("Back from keycloak.");
                       messageParent({
                           action: "authenticate",
                           data: App.keycloakAuth.tokens()
                       });
                   }
               } else { // in parent
                   if (!authenticated) {
                       logAuth("Opening dialog.")
                       Office.context.ui.displayDialogAsync(
                           window.location.origin,
                           {height: 70, width: 50},
                           function (result) {
                               const dialog = result.value;
                               dialog.addEventHandler(Office.EventType.DialogMessageReceived, function (arg) {
                                   const report = JSON.parse(arg.message);
                                   if (report.action === "log") {
                                       console.log(report.msg);
                                   } else if (report.action === "log-object") {
                                       console.log(report.msg, report.obj);
                                   }
                                   else if (report.action === "authenticate") {
                                       // logAuth("auth data from dialog", report.data);
                                       logAuth("Closing dialog.")
                                       dialog.close();
                                       App.keycloakAuth.init(report.data)
                                          .then(onLoggedIn);
                                   }
                               });
                           });
                   } else {
                       // Not expected to get here. We're in the parent, and
                       // we've authenticated, but without providing tokens.
                       // Perhaps react has decided to re-instantiate the App, and
                       // Keycloak is picking up tokens from cookies. In any case,
                       // we're logged in.
                       onLoggedIn();
                   }
               }
           }, function () {
               logAuth("Failed to init.");
           });
    }

    componentDidUpdate(prevProps) {
        console.log('componentDidUpdate called. props.isOfficeInitialized: ' + this.props.isOfficeInitialized + 
        ' state.documentLoadingState: ' + this.state.documentLoadingState.toLocaleString());
        if(this.props.isOfficeInitialized && !prevProps.isOfficeInitialized){
            this.initiateLogin(this.setAuthenticated);

            console.log("Office initialized, loading document state.");
            this.loadStateFromDocument()
            .then(()=>{
                console.log("Document loading successful.")
                this.setState({documentLoadingState:DocumentLoadingState.successful});
            })
            .catch((reason)=>{
                console.error('Failed loading document state from document.' + reason)
                this.setState({documentLoadingState: DocumentLoadingState.failed});
            });
        }
    }

    render() {
        const {
            title,
            isOfficeInitialized,
        } = this.props;

        if (!this.state.authenticated) {
            return (
                <Progress
                    title={title}
                    logo='assets/MTdiamondfilled.png'
                    message='Please wait, we are redirecting you to a login page.'
                />
            )
        }

        if (!isOfficeInitialized) {
            return (
                <Progress
                    title={title}
                    logo='assets/MTdiamondfilled.png'
                    message='Office initializing.'
                />
            );
        }  

        if (this.state.documentLoadingState === DocumentLoadingState.loading) {
            return (
                <Progress
                    title={title}
                    logo='assets/MTdiamondfilled.png'
                    message='Loading document properties.'
                />
            );
        }  
        
        if (this.state.documentLoadingState === DocumentLoadingState.failed) {
            return (
                <div className='mt-doc-tools'>
                    <div className='mt-doc-tools__main'>
                        <Error
                        title={title}
                        logo='assets/MTdiamondfilled.png'
                        message={'This does not appear to be a valid workflow file. File URL: ' + this.state.documentUrl}
                    />
                    </div>
                </div>
            );
        }     
        
        return (
            <div className='mt-doc-tools'>
                <div className='mt-doc-tools__main'>
                    <LanguageList keycloakClient={App.keycloakAuth} documentId={this.state.documentId}></LanguageList>
                </div>
            </div>
        );
    }

    loadStateFromDocument = async () => {
        //Get the URL of the current file.
        this.setState({documentLoadingState: DocumentLoadingState.loading});
        var fileUrl = "empty";
        if(Office && Office.context && Office.context.document){
            Office.context.document.getFilePropertiesAsync(function (asyncResult) {
                fileUrl = asyncResult.value.url;
                this.setState({documentUrl:fileUrl});
                console.log('Document loaded from: ' + fileUrl);
                this.setContextFromFileUrl(fileUrl);
            }.bind(this));
        }        
    }

    setContextFromFileUrl(url:string) :void {
        if (url.indexOf('wopi/') >= 0) {
            this.setContextFromWopiUrl(url);
        } else {
            // file should be in form 'c:\\somedirectory\file name.working-file-id.extension'
            // or "http(s)://someurl/file name.working-file-id.extension?maybe&some&query"
            // need to get working-file-id. I'm sure there is a regex way but brute force works.
            console.log('Setting context from local file: ' + url);

            let indexOfKey: number = url.indexOf("DID_");
            let documentId: string = '';
            if(indexOfKey >= 0) {
                documentId  = url.substr(indexOfKey + 4, 36);
                console.log('Document ID: ' + documentId);
            }

            this.setState({
                documentLoadingState: documentId === '' ? DocumentLoadingState.failed : DocumentLoadingState.successful,
                documentId: documentId
            });
        }
    }

    setContextFromWopiUrl(url: string) : void {
        // A 'compressed' token (containg both auth and refresh tokens) is
        // included in query string, but we delegate to keycloak.js to authenticate
        // and get a token.
        // const token:string = this.getQueryStringValue(url, 'access_token');
        const documentId:string = this.getQueryStringValue(url, 'doc-id');
        this.setState({documentId: documentId});
    }
    
    getQueryStringValue(queryString: string, key:string) :string {
        const tokenIndex = queryString.indexOf(key);
        let token:string = '';

        if (tokenIndex > 0) {
            token = queryString.substr(tokenIndex + key.length + 1);
            let ampIndex = token.indexOf('&');
            if (ampIndex > 0) {
                token = token.substr(0, ampIndex);
            }                
        }
        return token;
    }    

    public setAuthenticated(){
        this.setState({authenticated: true});
    }
}




// WEBPACK FOOTER //
// ./components/App.tsx